import { useCallback, useEffect, useState } from "react";
import { useIonViewWillEnter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { AssignmentPlatform } from "refsix-core";
import { SubscriptionItems } from "refsix-js-models";
import { useSubscription } from "../../hooks";
import { importAssignmentsInApp } from "../../../services/assignments/inAppAssignmentImport";
import { inAppImportMessage } from "../../../services/assignments/inAppImportMessage";
import { refreshSubscriptions } from "../../../services/subscriptionService";

/** What a platform records about the import it last ran, written server-side. */
export interface LastAssignmentImport {
    at: number;
    added?: number;
    updated?: number;
}

export interface AssignmentImport {
    importing: boolean;
    lastImport?: LastAssignmentImport;
    toastMessage?: string;
    dismissToast: () => void;
    startImport: () => Promise<void>;
}

/**
 * The in-app import, as a platform page uses it (REFSIX-568): the last-import
 * record, the running flag, and the one sentence the user gets when it ends.
 *
 * Shared by every scraped platform's page — the whole flow lives in
 * inAppAssignmentImport, so what is left here is state that would otherwise be
 * copied per platform.
 */
export function useAssignmentImport(
    platform: AssignmentPlatform
): AssignmentImport {
    const { t } = useTranslation();
    const subscriptions = useSubscription();
    const [importing, setImporting] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | undefined>();

    /**
     * On mount as well as on view-enter: a page inside an IonRouterOutlet is
     * kept alive outside the normal render cycle, so useIonViewWillEnter alone
     * does not reliably fire, and the record would then never update on a
     * second visit.
     */
    const refresh = useCallback(() => {
        refreshSubscriptions().catch((err) =>
            console.warn("Could not refresh subscriptions", err)
        );
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useIonViewWillEnter(() => {
        refresh();
    });

    const lastImport: LastAssignmentImport | undefined =
        subscriptions?.subscriptions?.find(
            (subscription: SubscriptionItems) =>
                subscription.provider === platform.id
        )?.rawData?.lastImport;

    /**
     * Opens the platform in an in-app WebView, reads the schedule out of it and
     * imports what it found. The whole flow lives in the service; this only has
     * to turn its outcome into one sentence.
     */
    const startImport = async () => {
        setImporting(true);
        try {
            const outcome = await importAssignmentsInApp(platform);
            const message = inAppImportMessage(
                outcome,
                t,
                platform.displayName
            );
            if (message) {
                setToastMessage(message);
            }

            if (outcome.status === "imported") {
                // The last-import record is written server-side, so it only
                // appears once the subscriptions are re-read.
                refresh();
            }
        } catch (err) {
            console.error("The in-app appointment import failed", err);
            setToastMessage(
                t("integrations.assignments.page.inAppImportFailed")
            );
        } finally {
            setImporting(false);
        }
    };

    return {
        importing,
        lastImport,
        toastMessage,
        dismissToast: () => setToastMessage(undefined),
        startImport,
    };
}
